import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Icons } from './Atoms';
import { goBack, isRootScreen } from '../lib/nav';
import { useApi } from '../lib/api';
import QuickPhotoModal from './QuickPhotoModal';
import { SidebarSprig } from './Illustrations';
import { useSeason } from '../lib/season';
import AnnouncementBanner from './AnnouncementBanner';

/**
 * Responsive Layout system.
 *
 * Breakpointy (mobile-first):
 *   < 768px           → phone: bottom tab bar, single column
 *   768-1023px        → tablet: sidebar nav 220px + main + optional right rail
 *   >= 1024px         → desktop: sidebar 260px + main + right rail (wider)
 *
 * Komponenty:
 *   <Layout>          - root layout, decyduje sidebar vs tabbar
 *   <MainColumn>      - środkowa kolumna z paddingiem
 *   <RightRail>       - opcjonalny prawy panel (tablet+desktop only)
 *   <PageHeader>      - header z breadcrumbs/akcjami (desktop)
 */

// ── Hook: aktualna szerokość viewportu (z SSR safety) ────────────
export function useViewport() {
  const [width, setWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return {
    width,
    isPhone:   width < 768,
    isTablet:  width >= 768 && width < 1024,
    isDesktop: width >= 1024,
    isLargeDesktop: width >= 1440,
  };
}

// ── Sidebar (tablet + desktop) ────────────────────────────────────
function Sidebar({ active, onQuickPhoto }) {
  // Pobierz prawdziwe dane użytkownika, działki, taski (count badge), reminders
  const { data: meRes } = useApi('/api/auth/me');
  const { data: plotsData } = useApi('/api/plots');
  const { data: tasksData } = useApi('/api/tasks?limit=200');
  const { data: remindersData } = useApi('/api/reminders');
  const { data: notifData } = useApi('/api/social/notifications');

  const user = meRes?.user;
  const todayIso = new Date().toISOString().slice(0, 10);
  const allTasks = tasksData?.data || tasksData || [];
  const todoToday = (Array.isArray(allTasks) ? allTasks : []).filter(t =>
    !t.completed && t.due_date && t.due_date.slice(0, 10) <= todayIso
  ).length;
  const remindersCount = Array.isArray(remindersData)
    ? remindersData.filter(r => !r.is_read).length
    : 0;
  const socialNotifCount = notifData?.unreadCount || 0;

  // SD4: skrócony pasek — 6 pozycji + "Więcej". Społeczność = 1 entry point (/feed z tabami).
  const nav = [
    { k: 'today',    l: 'Dziś',         to: '/dashboard',     Icon: Icons.Today,    count: todoToday || null },
    { k: 'cal',      l: 'Kalendarz',    to: '/calendar',      Icon: Icons.Calendar, count: remindersCount || null },
    { k: 'plants',   l: 'Rośliny',      to: '/plants',        Icon: Icons.Plants },
    { k: 'plot',     l: 'Działki',      to: '/plots',         Icon: Icons.Plot },
    { k: 'feed',     l: 'Społeczność',  to: '/feed',          Icon: Icons.Community, count: socialNotifCount || null },
    // "Więcej" → pełne menu z grupami. Reszta destynacji dostępna z menu.
    { k: 'menu',     l: 'Więcej',       to: '/menu',          Icon: Icons.More },
  ];
  const realPlots = (plotsData || []).slice(0, 6);
  const plotColors = ['var(--t-amber)', 'var(--t-rust)', 'var(--t-sage-deep)', 'var(--t-sage)'];

  const userInitials = (() => {
    if (!user) return '?';
    const src = user.public_display_name || user.username || user.email || '';
    return src.trim().slice(0, 2).toUpperCase();
  })();
  const userLabel = user?.public_display_name || user?.username || 'Twoje konto';
  const seasonNumber = (() => {
    if (!user?.created_at) return 1;
    const yr = new Date(user.created_at).getFullYear();
    return Math.max(1, new Date().getFullYear() - yr + 1);
  })();

  return (
    <aside style={{
      width: 'clamp(220px, 22vw, 280px)',
      borderRight: '1px solid var(--t-line)',
      background: 'var(--t-bg-elev)',
      height: 'var(--t-vh)',
      position: 'sticky',
      top: 0,
      display: 'flex',
      flexDirection: 'column',
      padding: '16px 0',
      flexShrink: 0,
      overflowY: 'auto',
    }}>
      {/* Logo / brand */}
      <Link to="/dashboard" style={{
        padding: '4px 24px 28px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        textDecoration: 'none',
      }}>
        <div style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: 'var(--t-sage)',
          display: 'grid',
          placeItems: 'center',
        }}>
          <Icons.Sprout color="var(--t-bg)" size={16} />
        </div>
        <div style={{
          fontFamily: 'var(--t-display)',
          fontSize: 22,
          fontWeight: 300,
          letterSpacing: '-.02em',
          color: 'var(--t-text)',
        }}>
          GardenApp<span style={{ color: 'var(--t-sage)', fontStyle: 'italic' }}>.pl</span>
        </div>
      </Link>

      {/* Main nav */}
      <nav style={{ padding: '0 12px' }}>
        {nav.map(n => {
          const on = n.k === active;
          return (
            <Link
              key={n.k}
              to={n.to}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 12px',
                borderRadius: 9,
                background: on ? 'rgba(168, 196, 158, 0.10)' : 'transparent',
                marginBottom: 2,
                textDecoration: 'none',
                transition: 'background 200ms ease',
              }}
              onMouseEnter={e => { if (!on) e.currentTarget.style.background = 'rgba(232, 230, 221, 0.03)'; }}
              onMouseLeave={e => { if (!on) e.currentTarget.style.background = 'transparent'; }}
            >
              <n.Icon color={on ? 'var(--t-sage)' : 'var(--t-text-dim)'} size={17} />
              <span style={{
                flex: 1,
                fontSize: 14,
                color: on ? 'var(--t-text)' : 'var(--t-text-dim)',
                letterSpacing: '-.01em',
                fontWeight: on ? 500 : 400,
              }}>{n.l}</span>
              {n.count != null && (
                <span style={{
                  fontFamily: 'var(--t-mono)',
                  fontSize: 10,
                  letterSpacing: '.1em',
                  color: on ? 'var(--t-sage)' : 'var(--t-text-mute)',
                }}>{n.count}</span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Szybkie akcje — Dodaj zdjęcie */}
      <div style={{ padding: '12px 12px 0' }}>
        <button
          type="button"
          onClick={onQuickPhoto}
          aria-label="Dodaj zdjęcie"
          style={{
            width: '100%',
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 12px', borderRadius: 9,
            background: 'rgba(168, 196, 158, 0.10)',
            border: '1px solid rgba(168, 196, 158, 0.25)',
            color: 'var(--t-sage)',
            cursor: 'pointer',
            fontFamily: 'var(--t-sans)', fontSize: 13,
            letterSpacing: '-.01em',
            transition: 'background 200ms ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(168, 196, 158, 0.18)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(168, 196, 158, 0.10)'; }}
        >
          <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
            <path d="M8.5 3v11M3 8.5h11" stroke="var(--t-sage)" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
          <span style={{ flex: 1, textAlign: 'left', fontWeight: 500 }}>Dodaj zdjęcie</span>
        </button>
      </div>

      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        padding: '12px 0 6px',
        pointerEvents: 'none',
      }}>
        <SidebarSprig size={86} pending={!!todoToday} />
      </div>

      {/* Twoje działki — realne dane */}
      {realPlots.length > 0 && (
        <div style={{ padding: '0 24px 16px' }}>
          <div style={{
            fontFamily: 'var(--t-mono)',
            fontSize: 10,
            letterSpacing: '.22em',
            textTransform: 'uppercase',
            color: 'var(--t-text-mute)',
            marginBottom: 10,
          }}>Twoje działki</div>
          {realPlots.map((p, i) => {
            const letter = String.fromCharCode(65 + i);
            const color = plotColors[i % plotColors.length];
            return (
              <Link
                key={p.id}
                to={`/plots/${p.id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '6px 0',
                  textDecoration: 'none',
                }}
              >
                <span style={{
                  fontFamily: 'var(--t-display)',
                  fontSize: 13,
                  fontWeight: 400,
                  color,
                  width: 14,
                  flexShrink: 0,
                }}>{letter}</span>
                <span style={{
                  fontSize: 12,
                  color: 'var(--t-text-dim)',
                  flex: 1,
                  letterSpacing: '-.01em',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>{p.name}</span>
              </Link>
            );
          })}
        </div>
      )}

      {/* User */}
      <Link
        to="/profile"
        style={{
          padding: '12px 24px',
          borderTop: '1px solid var(--t-line)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          textDecoration: 'none',
          minWidth: 0,
        }}
      >
        {user?.profile_photo ? (
          <img
            src={user.profile_photo.startsWith('/') ? user.profile_photo : `/${user.profile_photo}`}
            alt=""
            style={{
              width: 30, height: 30,
              borderRadius: '50%',
              objectFit: 'cover',
              flexShrink: 0,
            }}
          />
        ) : (
          <div style={{
            width: 30, height: 30,
            borderRadius: '50%',
            background: 'var(--t-sage)',
            color: 'var(--t-bg)',
            display: 'grid',
            placeItems: 'center',
            fontSize: 11,
            fontWeight: 600,
            fontFamily: 'var(--t-sans)',
            flexShrink: 0,
          }}>{userInitials}</div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 13,
            color: 'var(--t-text)',
            letterSpacing: '-.01em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>{userLabel}</div>
          <div style={{
            fontSize: 10,
            color: 'var(--t-text-mute)',
            fontFamily: 'var(--t-mono)',
            letterSpacing: '.12em',
            textTransform: 'uppercase',
          }}>Sezon {seasonNumber}</div>
        </div>
      </Link>
    </aside>
  );
}

// ── BottomTabBar (phone) ──────────────────────────────────────────
// Layout: 2 zakładki | + FAB (centralna) | 2 zakładki + Menu
// Replikuje wzorzec ze starego BottomNav (central gradient FAB).
function BottomTabBar({ active, onQuickPhoto }) {
  // Pobierz dane dla badge
  const { data: notifData } = useApi('/api/social/notifications');
  const socialNotifCount = notifData?.unreadCount || 0;

  // Lewa para
  const leftItems = [
    { k: 'today',  l: 'Dziś',    to: '/dashboard', Icon: Icons.Today },
    { k: 'plants', l: 'Rośliny', to: '/plants',    Icon: Icons.Plants },
  ];
  // Prawa para
  const rightItems = [
    { k: 'plot',  l: 'Działki',   to: '/plots',    Icon: Icons.Plot },
    { k: 'cal',   l: 'Kalendarz', to: '/calendar', Icon: Icons.Calendar, count: socialNotifCount },
  ];

  const renderItem = ({ k, l, to, Icon, count }) => {
    const on = k === active;
    const c = on ? 'var(--t-sage)' : 'var(--t-text-mute)';
    return (
      <Link
        key={k}
        to={to}
        style={{
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', gap: 4,
          minWidth: 48, flex: 1,
          textDecoration: 'none',
        }}
      >
        <Icon color={c} />
        <span style={{
          fontSize: 10,
          fontFamily: 'var(--t-mono)',
          letterSpacing: '.1em',
          textTransform: 'uppercase',
          color: c,
          fontWeight: 500,
        }}>{l}</span>
        {count > 0 && (
          <span style={{
            position: 'absolute', top: 4, right: 4,
            background: 'var(--t-rust)', color: '#fff',
            fontSize: 10, fontWeight: 700,
            minWidth: 16, height: 16, borderRadius: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 4px', lineHeight: 1,
          }}>{count > 9 ? '9+' : count}</span>
        )}
      </Link>
    );
  };

  return (
    <nav style={{
      position: 'fixed',
      bottom: 0, left: 0, right: 0,
      zIndex: 40,
      background: 'rgba(15, 20, 16, 0.85)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      borderTop: '1px solid var(--t-line)',
      paddingBottom: 'max(env(safe-area-inset-bottom), 12px)',
      paddingTop: 10,
      display: 'flex',
      alignItems: 'flex-start',
    }}>
      {leftItems.map(renderItem)}

      {/* Central FAB — Dodaj zdjęcie */}
      <button
        type="button"
        onClick={onQuickPhoto}
        aria-label="Dodaj zdjęcie"
        style={{
          flex: 1,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', gap: 4,
          background: 'transparent', border: 'none',
          cursor: 'pointer', padding: 0,
          position: 'relative',
        }}
      >
        <div style={{
          marginTop: -22,
          width: 54, height: 54, borderRadius: 16,
          background: 'linear-gradient(135deg, var(--t-sage) 0%, var(--t-sage-deep) 100%)',
          display: 'grid', placeItems: 'center',
          boxShadow: '0 8px 24px rgba(168, 196, 158, 0.35), 0 0 0 4px var(--t-bg-elev)',
          border: '1px solid var(--t-sage)',
        }}>
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <path d="M11 4v14M4 11h14" stroke="var(--t-bg)" strokeWidth="2.4" strokeLinecap="round" />
          </svg>
        </div>
        <span style={{
          fontSize: 10,
          fontFamily: 'var(--t-mono)',
          letterSpacing: '.1em',
          textTransform: 'uppercase',
          color: 'var(--t-sage)',
          fontWeight: 500,
          marginTop: 2,
        }}>Foto</span>
      </button>

      {rightItems.map(renderItem)}

      {/* Menu — bez ikony FAB, normalny tab */}
      {(() => {
        const Icon = Icons.Menu || Icons.Journal;
        const on = active === 'menu';
        const c = on ? 'var(--t-sage)' : 'var(--t-text-mute)';
        return (
          <Link
            to="/menu"
            style={{
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 4,
              minWidth: 48, flex: 1,
              textDecoration: 'none',
            }}
          >
            <Icon color={c} />
            <span style={{
              fontSize: 10,
              fontFamily: 'var(--t-mono)',
              letterSpacing: '.1em',
              textTransform: 'uppercase',
              color: c,
              fontWeight: 500,
            }}>Menu</span>
          </Link>
        );
      })()}
    </nav>
  );
}

// ── Main column ───────────────────────────────────────────────────
export function MainColumn({ children, maxWidth = 720, padding = '32px 32px 48px' }) {
  const { isPhone, isTablet } = useViewport();
  const compact = isPhone || isTablet;
  return (
    <main style={{
      flex: 1,
      maxWidth: compact ? '100%' : maxWidth,
      padding: compact ? '0 0 110px' : padding,
      overflow: 'auto',
      width: '100%',
    }}>
      {children}
    </main>
  );
}

// ── Right rail (desktop only — tablet renderuje mobilny layout) ──
export function RightRail({ children, width = 360 }) {
  const { isPhone, isTablet } = useViewport();
  if (isPhone || isTablet) return null;
  return (
    <aside style={{
      width,
      flexShrink: 0,
      padding: '32px 28px',
      borderLeft: '1px solid var(--t-line)',
      background: 'rgba(232, 230, 221, 0.015)',
      overflowY: 'auto',
    }}>
      {children}
    </aside>
  );
}

// ── Top bar (desktop tylko - breadcrumb + akcje) ──────────────────
export function TopBar({ left, right }) {
  const { isPhone, isTablet } = useViewport();
  if (isPhone || isTablet) return null;
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '18px 32px',
      borderBottom: '1px solid var(--t-line)',
      gap: 14,
    }}>
      <div>{left}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>{right}</div>
    </div>
  );
}

// ── MobileBackBar (NAV1) — cienki pasek wstecz na mobile (brak TopBar tam) ──
// PWA standalone nie ma paska przeglądarki ani natywnego wstecz — to jedyny powrót.
function MobileBackBar() {
  const navigate = useNavigate();
  const location = useLocation();
  if (isRootScreen(location.pathname)) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', height: 44,
      padding: '0 8px', borderBottom: '1px solid var(--t-line)', background: 'var(--t-bg)',
    }}>
      <button
        type="button"
        onClick={() => goBack(navigate, location, '/dashboard')}
        aria-label="Wstecz"
        style={{
          display: 'flex', alignItems: 'center', gap: 4,
          minWidth: 44, height: 44, padding: '0 8px',
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: 'var(--t-text-dim)', fontFamily: 'var(--t-sans)', fontSize: 14,
        }}
      >
        <Icons.Back size={20} /> Wstecz
      </button>
    </div>
  );
}

// ── Layout root ───────────────────────────────────────────────────
export default function Layout({ children, active = 'today', topBar = null, rightRail = null }) {
  const { isPhone, isTablet } = useViewport();
  const [quickPhotoOpen, setQuickPhotoOpen] = useState(false);
  useSeason(); // ustawia html[data-season] → fenologiczne tinty tła z tokens.css

  if (isPhone || isTablet) {
    // Phone + iPad portrait: full-width content, fixed bottom nav.
    // Sidebar 220-280px na 768px viewport zostawia za mało miejsca na treść.
    // padding-bottom: 76px (wysokość BottomTabBar) + safe-area (home indicator iPhone/iPad)
    // + 16px oddechu, aby ostatnie elementy ekranu nie chowały się pod nav.
    // padding-top: env(safe-area-inset-top) — treść nie chowa się pod status barem /
    // notchem / Dynamic Island w trybie PWA standalone (status-bar-style=black-translucent).
    return (
      <div style={{
        minHeight: 'var(--t-vh)',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'calc(76px + env(safe-area-inset-bottom, 0px) + 16px)',
      }}>
        <MobileBackBar />
        <AnnouncementBanner />
        {children}
        <BottomTabBar active={active} onQuickPhoto={() => setQuickPhotoOpen(true)} />
        <QuickPhotoModal
          open={quickPhotoOpen}
          onClose={() => setQuickPhotoOpen(false)}
          onSuccess={() => setQuickPhotoOpen(false)}
        />
      </div>
    );
  }

  // Tablet + desktop: sidebar + main (+ optional right rail)
  return (
    <div style={{ display: 'flex', minHeight: 'var(--t-vh)' }}>
      <Sidebar active={active} onQuickPhoto={() => setQuickPhotoOpen(true)} />
      <QuickPhotoModal
        open={quickPhotoOpen}
        onClose={() => setQuickPhotoOpen(false)}
        onSuccess={() => setQuickPhotoOpen(false)}
      />
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {topBar && topBar}
        <AnnouncementBanner />
        <div style={{
          flex: 1,
          display: 'flex',
          overflow: 'hidden',
        }}>
          <div style={{
            flex: 1,
            overflowY: 'auto',
          }}>
            {children}
          </div>
          {rightRail && <RightRail>{rightRail}</RightRail>}
        </div>
      </div>
    </div>
  );
}
